import { useState } from "react";
import { get, ref } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { call, money } from "../utils/appData";
export default function HireCheckout({ job, jobId, applicant }) {
  const { currentUser } = useUser();
  const [open, setOpen] = useState(false);
  const [seller, setSeller] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const creatorId =
    applicant.applicantId || applicant.uid || applicant.userId || applicant.id;
  const stripe = Boolean(seller?.stripeConnectedAccountId);
  const currency = stripe ? "USD" : "ZAR";
  const base = Number(amount);
  const total = Math.round(base * 1.08 * 100) / 100;
  async function start() {
    setBusy(true);
    setError("");
    try {
      const s = (await get(ref(database, `Users/${creatorId}`))).val();
      if (!s?.stripeConnectedAccountId && !s?.paystackSubaccountCode)
        throw new Error("This creator needs to complete their payout setup.");
      setSeller(s);
      setOpen(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function pay(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!Number.isFinite(base) || base <= 0)
        throw new Error("Enter the agreed amount.");
      let url;
      if (stripe) {
        const r = await call("createJobCheckoutSession", {
          userId: currentUser.uid,
          email: currentUser.email,
          jobId,
          jobTitle: job.title,
          creatorId,
          basePrice: base,
          currency: "usd",
          inApp: false,
          platform: "web",
          successUrl: `${window.location.origin}/#/jobs/${jobId}?payment=processing`,
          cancelUrl: `${window.location.origin}/#/jobs/${jobId}`,
        });
        url = r.url;
      } else {
        const { quote } = await call("createPaymentQuote", {
          type: "job_secure",
          amount: Math.round(base * 100),
          currency: "ZAR",
          buyerId: currentUser.uid,
          sellerId: creatorId,
          jobId,
        });
        if (quote.amount !== Math.round(total * 100))
          throw new Error(
            "Payment fees have changed. Reload and check the total before paying.",
          );
        const params = new URLSearchParams({
          jobId,
          creatorId,
          type: "job_secure",
          quoteId: quote.quoteId,
        });
        const r = await call("initializePaystackPayment", {
          email: currentUser.email,
          amount: quote.amount,
          currency: quote.currency,
          quoteId: quote.quoteId,
          subaccount: seller.paystackSubaccountCode,
          bearer: "account",
          metadata: {
            jobId,
            buyerId: currentUser.uid,
            creatorId,
            type: "job_secure",
            quoteId: quote.quoteId,
            platform: "web",
          },
          callback_url: `${window.location.origin}/paystack-redirect.html?${params}`,
        });
        url = r.data?.authorization_url;
      }
      if (!url) throw new Error("Unable to open checkout.");
      window.location.assign(url);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }
  return (
    <>
      <button
        className="zy-button inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[14px] px-[17px] py-[11px] text-sm font-semibold disabled:cursor-wait disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] bg-[#327691] text-white"
        disabled={
          busy || Boolean(job.hiredApplicantId) || job.status !== "active"
        }
        onClick={start}
      >
        Hire & secure payment
      </button>
      {error && (
        <p
          role="alert"
          className="zy-error my-3 rounded-[10px] bg-red-100 p-3 text-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {error}
        </p>
      )}
      {open && (
        <div
          className="zy-modal fixed inset-0 z-[10000] grid place-items-center bg-black/50 p-4 md:p-5"
          role="dialog"
          aria-modal="true"
          aria-label="Hire creator"
        >
          <form
            className="zy-modal-content max-h-[90dvh] w-full max-w-[650px] overflow-y-auto overscroll-contain rounded-[20px] bg-white p-6 text-[#172831] dark:bg-[#23272c] dark:text-[#f3f6f8] zy-form grid gap-4 [&_label]:text-sm [&_label]:font-semibold"
            onSubmit={pay}
          >
            <div className="zy-between flex items-center justify-between gap-4">
              <h2>Hire {seller?.name || "creator"}</h2>
              <button
                type="button"
                disabled={busy}
                onClick={() => setOpen(false)}
              >
                Close
              </button>
            </div>
            <p>{job.title}</p>
            <label>
              Agreed rate ({currency})
              <input
                required
                min="0.01"
                max="1000000"
                step="0.01"
                type="number"
                className="zy-input mt-1.5 block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-inherit focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] dark:border-[#363e46] dark:bg-[#23272c]"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
            <p>Service fee (8%): {money(base * 0.08, currency)}</p>
            <strong>Total: {money(total, currency)}</strong>
            <p className="zy-muted text-sm text-slate-500 dark:text-[#a4b0bc]">
              The creator receives the agreed rate less their 7% fee. Hiring is
              confirmed by the payment server.
            </p>
            {error && (
              <p className="zy-error my-3 rounded-[10px] bg-red-100 p-3 text-red-700 dark:bg-red-950 dark:text-red-200">
                {error}
              </p>
            )}
            <button
              disabled={busy || base <= 0}
              className="zy-button inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[14px] px-[17px] py-[11px] text-sm font-semibold disabled:cursor-wait disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#49a3c9] bg-[#327691] text-white"
            >
              {busy
                ? "Opening checkout…"
                : `Continue to ${stripe ? "Stripe" : "Paystack"}`}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
